import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../Services/api.service";
import Title from "../components/PageTitle";

const TicketLogs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const fetchTicket = async () => {
    try {
      const data = await apiService.fetchChat(id, token);
      if (data.success) {
        setTicket(data.ticket);
      } else {
        setTicket(null);
      }
    } catch (err) {
      console.error("Failed to fetch ticket:", err);
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await apiService.addTicketNote(id, noteText.trim(), token);
      if (res.success) {
        setTicket(res.ticket);
        setNoteText("");
      }
    } catch (err) {
      console.error("Failed to add note:", err);
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <Typography color="text.secondary">
          Ticket not found or failed to load.
        </Typography>
      </Box>
    );
  }

  const notes = [...(ticket.internalNotes || [])].reverse();

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 4, fontFamily: "'Inter', sans-serif" }}>
      <Title text="Ticket Logs" />

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <IconButton onClick={() => navigate("/dashboard/support")}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {ticket.subject}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ticket ID: {ticket.ticketId}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
            Doctor:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {ticket.doctor?.name || "N/A"}
            </Box>
            {"  ·  "}
            Clinic:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {ticket.doctor?.clinics?.[0]?.name || "N/A"}
            </Box>
          </Typography>
        </Box>
        <Chip
          label={ticket.status?.toUpperCase() || "OPEN"}
          color={
            ticket.status === "open"
              ? "success"
              : ticket.status === "closed"
              ? "default"
              : "warning"
          }
          size="small"
          sx={{ ml: { xs: 0, sm: "auto" } }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Internal notes ({notes.length})
      </Typography>

      <Box sx={{ mb: 3 }}>
        {notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        ) : (
          notes.map((note, idx) => (
            <Box
              key={idx}
              sx={{
                borderLeft: "3px solid #F59E0B",
                pl: 1.5,
                mb: 1.5,
              }}
            >
              <Typography variant="body2">{note.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                {note.by || "unknown"} ·{" "}
                {dayjs(note.createdAt).format("DD MMM YYYY, hh:mm A")}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      {ticket.status === "in-progress" && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Log a step/update..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={addingNote || !noteText.trim()}
            sx={{ minHeight: 44, width: { xs: "100%", sm: "auto" } }}
          >
            Add
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default TicketLogs;
